import { Link } from "@tanstack/react-router";
import { Cake, Sparkles } from "lucide-react";
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	type TooltipProps,
	XAxis,
	YAxis,
} from "recharts";

import type { DashboardPulse } from "@/api/db/dashboard-pulse";
import { format } from "@/lib/format";
import { DashboardLabels } from "@/lib/labels";
import { finBrl } from "@/lib/labels/financeiro";
import { cn } from "@/lib/utils";

import {
	DashboardCard,
	DashboardCardBody,
	DashboardCardHeader,
	DashboardCardTitle,
} from "./dashboard-card";

const AXIS = { fill: "var(--muted-foreground)", fontFamily: "var(--font-mono)", fontSize: 12 };
const GRID = "var(--border)";
const COUNT = new Intl.NumberFormat("pt-BR");

const funnelOrder = [
	"interest",
	"appointment",
	"consulted",
	"quoted",
	"closed",
	"post_op",
	"recurring",
	"reactivation",
	"lost",
	"not_applicable",
] as const;
const temperatureOrder = ["hot", "warm", "cold"] as const;

const temperatureTone: Record<(typeof temperatureOrder)[number], string> = {
	hot: "bg-destructive",
	warm: "bg-warning",
	cold: "bg-primary",
};

function ChartCard({
	component,
	title,
	meta,
	children,
}: {
	component: string;
	title: string;
	meta?: string;
	children: React.ReactNode;
}) {
	return (
		<DashboardCard data-component={component}>
			<DashboardCardHeader>
				<DashboardCardTitle>{title}</DashboardCardTitle>

				{meta !== undefined && (
					<span className="font-mono text-xs text-muted-foreground">{meta}</span>
				)}
			</DashboardCardHeader>

			<DashboardCardBody>{children}</DashboardCardBody>
		</DashboardCard>
	);
}

function BarRow({
	label,
	total,
	max,
	tone,
	muted,
}: {
	label: React.ReactNode;
	total: number;
	max: number;
	tone: string;
	muted?: boolean;
}) {
	return (
		<li data-slot="bar" data-total={total} className="min-w-0">
			<div className="flex items-baseline justify-between gap-2">
				<span
					className={cn("truncate text-sm", muted ? "text-muted-foreground" : "text-foreground")}
				>
					{label}
				</span>

				<span className="shrink-0 font-mono text-xs text-muted-foreground">{total}</span>
			</div>

			<div className="mt-1 h-1.5 overflow-hidden rounded-full bg-border">
				<div
					className={cn("h-full rounded-full", tone)}
					style={{ width: `${(total / max) * 100}%` }}
				/>
			</div>
		</li>
	);
}

export function FunnelCard({ items }: { items: DashboardPulse["funnel"] }) {
	const byStage = new Map(items.map((item) => [item.funnel_stage, item.total]));
	const max = Math.max(1, ...items.map((item) => item.total));

	return (
		<ChartCard component="dashboard-funnel" title={DashboardLabels.funnel.title}>
			<ol className="flex flex-col gap-3">
				{funnelOrder.map((stage) => (
					<BarRow
						key={stage}
						label={
							<Link
								to="/pessoas"
								search={(prev) => ({ ...prev, stage, page: 1 })}
								className="text-foreground underline-offset-2 hover:text-primary hover:underline"
							>
								{DashboardLabels.funnel.stage[stage]}
							</Link>
						}
						total={byStage.get(stage) ?? 0}
						max={max}
						tone="bg-primary"
					/>
				))}
			</ol>
		</ChartCard>
	);
}

export function TemperatureCard({ items }: { items: DashboardPulse["temperature"] }) {
	const byLevel = new Map(items.map((item) => [item.temperatura, item.total]));
	const max = Math.max(1, ...items.map((item) => item.total));

	return (
		<ChartCard component="dashboard-temperature" title={DashboardLabels.temperature.title}>
			<ol className="flex flex-col gap-3">
				{temperatureOrder.map((level) => (
					<BarRow
						key={level}
						label={DashboardLabels.temperature.level[level]}
						total={byLevel.get(level) ?? 0}
						max={max}
						tone={temperatureTone[level]}
					/>
				))}
			</ol>
		</ChartCard>
	);
}

export function RecencyCard({ items }: { items: DashboardPulse["recency"] }) {
	const max = Math.max(1, ...items.map((item) => item.total));

	return (
		<ChartCard component="dashboard-recency" title={DashboardLabels.recency.title}>
			<ol className="flex flex-col gap-3">
				{items.map((item, index) => (
					<BarRow
						key={item.band}
						label={DashboardLabels.recency.band[item.band]}
						total={item.total}
						max={max}
						tone={index === items.length - 1 ? "bg-destructive/50" : "bg-info"}
						muted={index === items.length - 1}
					/>
				))}
			</ol>
		</ChartCard>
	);
}

function countTooltip(suffix: string) {
	return function TooltipContent({ active, payload, label }: TooltipProps<number, string>) {
		if (!active || !payload || payload.length === 0) {
			return null;
		}

		return (
			<div className="rounded-md border border-border bg-card px-2.5 py-1.5 text-xs shadow-control">
				<p className="font-medium text-foreground">{label}</p>

				<p className="font-mono text-muted-foreground">
					{payload[0].value} {suffix}
				</p>
			</div>
		);
	};
}

export function ActiveByMonth({ months }: { months: DashboardPulse["active_by_month"] }) {
	const data = months.map((month) => ({ ...month, label: DashboardLabels.month(month.month) }));

	return (
		<ChartCard component="dashboard-active-by-month" title={DashboardLabels.charts.activeByMonth}>
			<ResponsiveContainer width="100%" height={260}>
				<AreaChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
					<defs>
						<linearGradient id="grad-live" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor="var(--success)" stopOpacity={0.35} />
							<stop offset="100%" stopColor="var(--success)" stopOpacity={0.02} />
						</linearGradient>
					</defs>

					<CartesianGrid stroke={GRID} vertical={false} />
					<XAxis
						dataKey="label"
						tick={AXIS}
						axisLine={{ stroke: GRID }}
						tickLine={false}
						minTickGap={16}
					/>
					<YAxis tick={AXIS} axisLine={false} tickLine={false} allowDecimals={false} />
					<Tooltip
						cursor={{ stroke: "var(--primary-soft)", strokeWidth: 1 }}
						content={countTooltip("conversas")}
					/>
					<Area
						type="monotone"
						dataKey="total"
						stroke="var(--success)"
						strokeWidth={2}
						fill="url(#grad-live)"
						dot={false}
					/>
				</AreaChart>
			</ResponsiveContainer>
		</ChartCard>
	);
}

function FinanceTooltip({ active, payload, label }: TooltipProps<number, string>) {
	if (!active || !payload || payload.length === 0) {
		return null;
	}

	return (
		<div className="rounded-md border border-border bg-card px-2.5 py-1.5 text-xs shadow-control">
			<p className="font-medium text-foreground">{label}</p>

			{payload.map((entry) => (
				<p key={entry.dataKey} className="font-mono text-muted-foreground">
					{entry.name}: {finBrl.format((entry.value ?? 0) / 100)}
				</p>
			))}
		</div>
	);
}

export function FinanceByMonth({ months }: { months: DashboardPulse["finance_by_month"] }) {
	const data = months.map((month) => ({ ...month, label: DashboardLabels.month(month.month) }));

	return (
		<ChartCard component="dashboard-finance" title={DashboardLabels.finance.title}>
			<ResponsiveContainer width="100%" height={260}>
				<BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
					<CartesianGrid stroke={GRID} vertical={false} />
					<XAxis
						dataKey="label"
						tick={AXIS}
						axisLine={{ stroke: GRID }}
						tickLine={false}
						minTickGap={16}
					/>
					<YAxis
						tick={AXIS}
						axisLine={false}
						tickLine={false}
						tickFormatter={(value: number) => `${Math.round(value / 100_000)}k`}
					/>
					<Tooltip cursor={{ fill: "var(--primary-soft)" }} content={<FinanceTooltip />} />
					<Bar
						dataKey="in_cents"
						name={DashboardLabels.finance.in}
						fill="var(--chart-2)"
						radius={[4, 4, 0, 0]}
						maxBarSize={22}
					/>
					<Bar
						dataKey="out_cents"
						name={DashboardLabels.finance.out}
						fill="var(--chart-4)"
						radius={[4, 4, 0, 0]}
						maxBarSize={22}
					/>
				</BarChart>
			</ResponsiveContainer>
		</ChartCard>
	);
}

export function BirthdaysCard({ items }: { items: DashboardPulse["birthdays"] }) {
	return (
		<DashboardCard data-component="dashboard-birthdays">
			<DashboardCardHeader>
				<span className="flex items-center gap-2">
					<span className="grid size-10 shrink-0 place-items-center rounded-md bg-destructive-soft text-destructive">
						<Cake className="size-4" />
					</span>

					<DashboardCardTitle>{DashboardLabels.birthdays.title}</DashboardCardTitle>
				</span>

				<span className="font-mono text-xs text-muted-foreground">{items.length}</span>
			</DashboardCardHeader>

			<DashboardCardBody>
				{items.length === 0 && (
					<p className="text-sm text-muted-foreground">{DashboardLabels.birthdays.none}</p>
				)}

				{items.length > 0 && (
					<ol className="grid max-h-60 grid-cols-1 gap-x-6 gap-y-1.5 overflow-y-auto sm:grid-cols-2">
						{items.map((person) => (
							<li key={person.id} className="flex min-w-0 items-baseline gap-2">
								<span className="w-6 shrink-0 text-right font-mono text-xs text-muted-foreground">
									{person.day}
								</span>

								<Link
									to="/pessoa/$id"
									params={{ id: person.id }}
									className="truncate text-sm text-foreground hover:text-primary hover:underline"
								>
									{format.readableName(person.name) ?? person.name}
								</Link>
							</li>
						))}
					</ol>
				)}
			</DashboardCardBody>
		</DashboardCard>
	);
}

export function AiCard({ ai }: { ai: DashboardPulse["ai"] }) {
	return (
		<DashboardCard data-component="dashboard-ai">
			<DashboardCardHeader>
				<span className="flex items-center gap-2">
					<span className="grid size-10 shrink-0 place-items-center rounded-md bg-primary-soft text-primary">
						<Sparkles className="size-4" />
					</span>

					<DashboardCardTitle>{DashboardLabels.ai.title}</DashboardCardTitle>
				</span>
			</DashboardCardHeader>

			<DashboardCardBody>
				<p className="metric-value text-foreground">{COUNT.format(ai.total)}</p>

				<p className="mt-1 text-xs text-muted-foreground">{DashboardLabels.ai.triages}</p>

				{ai.errors > 0 && (
					<p data-slot="errors" className="text-xs text-muted-foreground">
						{ai.errors} {DashboardLabels.ai.errors}
					</p>
				)}
			</DashboardCardBody>
		</DashboardCard>
	);
}
